// orchestrator_fullstack/frontend/src/app/auth/RequirePermission.tsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import ForbiddenPage from "../pages/ForbiddenPage";
import { can, normalizeRoles, Permission, Role } from "./rbac";
import { useAuth } from "./AuthProvider";

/**
 * Backwards-compatible route/section guard.
 *
 * Supports BOTH historical patterns used across the repo:
 *
 *  1) Permission-based:
 *       <RequirePermission permission="admin.overview.view" />
 *
 *  2) Role-based shortcut:
 *       <RequirePermission anyOf={["admin","platform-admin"]} />
 *
 * This avoids mechanical refactors and fixes the
 * "Required: undefined" Forbidden bug.
 */
function RequirePermissionImpl(props: {
  children: React.ReactNode;

  // Style A: fine-grained permission gating
  permission?: Permission;

  // Style B: role-based shortcut gating
  anyOf?: Array<Role | "tenant-admin" | string>;
}) {
  const { state } = useAuth();
  const loc = useLocation();

  const token = state.token;
  const isAuthed = Boolean(token && state.user);

  // Best-effort loading window:
  // token present but /auth/me not resolved yet
  const isLoading = Boolean(token && !state.fetchedAt && !state.user);

  if (isLoading) return null;

  if (!isAuthed) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  const roles = normalizeRoles(state.roles);

  // ------------------------------------------------------------
  // Role-based gating (anyOf)
  // ------------------------------------------------------------
  if (props.anyOf && props.anyOf.length > 0) {
    const want = new Set(
      props.anyOf.map((r) => {
        const s = String(r || "").trim().toLowerCase();
        // UI alias support
        return s === "tenant-admin" ? "admin" : s;
      })
    );

    const have = new Set(roles.map((r) => r.toLowerCase()));
    const ok = Array.from(want).some((r) => have.has(r));

    if (!ok) {
      return (
        <ForbiddenPage
          required={Array.from(want)}
          roles={roles}
          tenant={state.tenant}
        />
      );
    }

    return <>{props.children}</>;
  }

  // ------------------------------------------------------------
  // Permission-based gating
  // ------------------------------------------------------------
  const perm = props.permission;

  if (!perm) {
    // Fail closed with a *clear diagnostic* instead of silent denial
    return (
      <ForbiddenPage
        required={["(miswired guard: missing permission or anyOf)"]}
        roles={roles}
        tenant={state.tenant}
      />
    );
  }

  const allowed = can(roles, perm);
  if (!allowed) {
    return (
      <ForbiddenPage
        required={[perm]}
        roles={roles}
        tenant={state.tenant}
      />
    );
  }

  return <>{props.children}</>;
}

// ✅ default export (for imports like: import RequirePermission from "...";)
export default RequirePermissionImpl;

// ✅ named export (for imports like: import { RequirePermission } from "...";)
export const RequirePermission = RequirePermissionImpl;